import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ArrowLeft, ChevronDown, Download, Lock, Loader2, AlertTriangle,
  Settings as SettingsIcon, Sparkles, Upload as UploadIcon,
} from 'lucide-react';

import {
  getActiveGenerateAiJob,
  getDrawingFigures,
  getDrawingNumerals,
  getDrawingSourceFigures,
  getDrawingState,
  getDrawingTermMappings,
} from '@/api/client';
import {
  hydrateAutoSaveForMatter,
  subscribeToAutoSaveQueue,
  useDrawingStore,
} from '@/store/drawingStore';
import { discardPendingMutations } from '@/lib/drawingAutoSave';
import { ensureMatterState } from './ensureMatterState';
// SEC-F02 (Phase 28 3-I) — defang raw backend error strings
// before rendering them in the load-failed panel.
import { sanitizeApiError } from '@/lib/sanitizeApiError';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import FigureGallery from './FigureGallery';
import Canvas from './Canvas';
import KonvaCanvas from './KonvaCanvas';
import ReferencePanel from './ReferencePanel';
import GenerateAiModal from './GenerateAiModal';
import { FEATURES } from '@/config/features';
import GenerationJobPill from './GenerationJobPill';
import AnalyzeFigureModal from './AnalyzeFigureModal';
import ClaimEditModal from './ClaimEditModal';
import SpecRevisionBanner from '@/components/spec/SpecRevisionBanner';
import { buildDrawingExportMenuItems } from './exportMenu';
import CoursesLink from '@/components/layout/CoursesLink';
import {
  VIEWPORT_MIN_WIDTH_PX,
  ViewportTooNarrow,
  useViewportGate,
} from '@/components/shared/ViewportTooNarrow';

interface Props {
  matterId: string;
  onBack?: () => void;
  /** The user-guide videos (the Courses link). */
  onOpenCourses?: () => void;
  /** Admin-only — opens the drawing settings page. Undefined for
   * non-admins, in which case the gear button is omitted. */
  onOpenSettings?: () => void;
  /** Cooperative navigation to the Spec workbench. Surfaced as the
   * "Open Specification →" button in the header when at least one
   * drawing is frozen for the matter. */
  onOpenSpec?: () => void;
}

/** Phase 4 viewport gate per the brief — Drawing is desktop-only.
 * Anything below 1280 px renders a stub instead of the workbench.
 * The constant + hook + stub live in
 * `frontend/src/components/shared/ViewportTooNarrow.tsx`; both this
 * module and the Spec module (Phase 4) consume the shared primitives. */

const outlineBtn =
  'inline-flex h-9 items-center justify-center gap-1 rounded-sm border border-zinc-300 ' +
  'bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 ' +
  'transition-colors';

/**
 * Drawing workbench — slice 4A shell.
 *
 * Renders three states:
 *   1. Viewport too narrow → desktop-only stub (no API call).
 *   2. Loading + locked + error states surfaced from the
 *      drawing store after a single GET /drawing/state call on mount.
 *   3. Unlocked + workbench placeholders that slices 4B–4D fill in
 *      (FigureGallery, Canvas, ReferencePanel).
 *
 * The codebase does NOT use react-router; navigation is driven by the
 * App.js `view` state. The `onBack` prop sets the view back to the
 * matter workflow — same pattern Settings / MattersPage use.
 */
export default function DrawingPage({ matterId, onBack, onOpenSettings, onOpenSpec, onOpenCourses }: Props) {
  const {
    hydrationStatus,
    lockedReason,
    rollup,
    errorMessage,
    figures,
    sourceFigures,
    activeFigureId,
    numerals,
    termMappings,
    canvasPrimitives,
    referencePanelTab,
    pendingImportSourceIds,
    pendingSyncIds,
    uploading,
    mutationError,
    setHydrating,
    setLocked,
    setUnlocked,
    setError,
    setFigures,
    setSourceFigures,
    setActiveFigureId,
    setNumerals,
    setTermMappings,
    setReferencePanelTab,
    setMutationError,
    importFromIdf,
    uploadFigure,
    syncSource,
    discardFigure,
    reprimeFigure,
    acknowledgeSourceRemoved,
    reset,
    loadCanvasFor,
    flushCanvasNow,
    saveStatus,
    pendingMutationCount,
    lastSavedAt,
    generationModalOpen,
    analyzeModalFigureId,
    claimEditModalClaimId,
    setGenerationJob,
    openGenerationModal,
  } = useDrawingStore();

  const activeFigure = activeFigureId
    ? figures.find((f) => f.id === activeFigureId) ?? null
    : null;

  // ── Auto-save queue lifecycle ────────────────────────────────────
  // Subscribe once on mount so the saveStatus / pendingMutationCount
  // / lastSavedAt fields stay in sync with the queue. Also hydrate
  // any persisted offline mutations from the previous session.
  useEffect(() => {
    const unsub = subscribeToAutoSaveQueue();
    void hydrateAutoSaveForMatter(matterId);
    return () => { unsub(); };
  }, [matterId]);

  // BUG-001 — PostHog session_recording is now disabled at init
  // (see public/index.html). The previous per-component
  // stopSessionRecording dance was insufficient: PostHog
  // re-attaches its MutationObserver on any state update,
  // racing the component's stop call across re-renders. The
  // init-level disable is the only deterministic kill switch.
  //
  // The defensive stopSessionRecording call below stays as
  // belt-and-braces in case a PostHog remote-config override
  // ever re-enables recording at runtime. The matching
  // startSessionRecording on unmount was REMOVED — restarting
  // recording on every drawing-page unmount was the exact
  // mechanism that re-introduced the 30-120s compositor freeze
  // when the drafter switched back to drawings.
  useEffect(() => {
    const ph = (typeof window !== 'undefined'
      ? (window as unknown as {
          posthog?: { stopSessionRecording?: () => void };
        }).posthog
      : undefined);
    try {
      ph?.stopSessionRecording?.();
    } catch {
      // Never let an analytics SDK throw a Drawing-page mount.
    }
  }, []);

  // Force-flush before unmount so a quick "back to matter" doesn't
  // leave drifting mutations in the queue.
  useEffect(() => {
    return () => { void flushCanvasNow(); };
  }, [flushCanvasNow]);

  // FT-007 (B3) — guard external navigation (tab close, browser
  // refresh, OS-level forward/back) while the auto-save queue has
  // un-flushed entries. The native ``beforeunload`` confirm is the
  // only portable hook for external navigation in a state-machine
  // app (we explicitly DON'T use react-router-dom's ``useBlocker``
  // per the brief). The in-app Back button is wrapped separately
  // below (``guardedOnBack``) so the drafter sees a friendlier
  // confirm before the state machine swaps view away.
  useEffect(() => {
    if (pendingMutationCount === 0) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      // Modern browsers ignore the message string and show their
      // own "Leave site?" dialog; setting returnValue + calling
      // preventDefault is enough to trigger it.
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [pendingMutationCount]);

  // FT-007 (B3) — in-app Back guard. The state-machine ``onBack``
  // prop is what swaps view='drawings' → view='matter' in App.js;
  // wrap it so the drafter sees a confirm when the queue has
  // dirty entries that haven't been flushed yet. We still always
  // call the original ``onBack`` after the confirm — the unmount
  // effect above runs ``flushCanvasNow`` so the queue drains as
  // part of the navigation.
  const guardedOnBack = useCallback(() => {
    if (!onBack) return;
    if (pendingMutationCount > 0) {
      // eslint-disable-next-line no-alert -- intentional confirm dialog
      const ok = window.confirm(
        `You have ${pendingMutationCount} unsaved drawing change`
        + `${pendingMutationCount === 1 ? '' : 's'}. Leave anyway?`,
      );
      if (!ok) return;
    }
    onBack();
  }, [onBack, pendingMutationCount]);

  // Lazy-load canvas state when the active figure changes.
  useEffect(() => {
    if (!activeFigureId) return;
    // Flush any in-flight queue for the previous figure before
    // hydrating the new one — keeps the local cache and persisted
    // state in lockstep across figure switches.
    void flushCanvasNow().then(() => {
      void loadCanvasFor(matterId, activeFigureId);
    });
  }, [activeFigureId, matterId, loadCanvasFor, flushCanvasNow]);

  // ── Viewport guard (shared with Spec module via ViewportTooNarrow) ─
  const viewportTooNarrow = useViewportGate(VIEWPORT_MIN_WIDTH_PX);

  // ── Hydration: state + figures + source-figures in parallel ─────
  const hydrate = useCallback(async () => {
    if (!matterId) return;
    setHydrating(matterId);
    try {
      // Fetch /state first; if locked, the figures + source-figures
      // endpoints would just return the same locked payload, so skip
      // them. When unlocked, fetch all three in parallel.
      const stateResponse = await getDrawingState(matterId);
      if (stateResponse.locked) {
        setLocked(matterId, stateResponse.locked_reason || 'claims_not_frozen');
        return;
      }
      setUnlocked(matterId, {
        figures_count: stateResponse.figures_count ?? 0,
        frozen_count: stateResponse.frozen_count ?? 0,
        unanalyzed_count: stateResponse.unanalyzed_count ?? 0,
        issues_count: stateResponse.issues_count ?? 0,
        active_figure_id: stateResponse.active_figure_id ?? null,
        last_activity_at: stateResponse.last_activity_at ?? null,
      });
      // NB-27 — the numeral edit panel, the Claims tab and the term
      // glossary read the claims from the matter state, which only the
      // matter page loaded. After a page refresh (the #/drawings/<id>
      // deep link) it was empty and editing a numeral offered no claim
      // terms. Load it here when it is missing or for another matter.
      void ensureMatterState(matterId);

      const [figuresResponse, sourceFiguresResponse, numeralsResponse, termMappingsResponse] = await Promise.all([
        getDrawingFigures(matterId),
        getDrawingSourceFigures(matterId),
        getDrawingNumerals(matterId),
        getDrawingTermMappings(matterId),
      ]);
      // Each list endpoint can also return a locked payload — defend
      // against the race where claims unfreeze between the state call
      // and the list call. If we hit that, swap the page back to the
      // locked panel rather than rendering a stale gallery.
      if (Array.isArray(figuresResponse)) {
        setFigures(figuresResponse);
      } else if (figuresResponse?.locked) {
        setLocked(matterId, figuresResponse.locked_reason || 'claims_not_frozen');
        return;
      }
      if (Array.isArray(sourceFiguresResponse)) {
        setSourceFigures(sourceFiguresResponse);
      } else if (sourceFiguresResponse?.locked) {
        setLocked(matterId, sourceFiguresResponse.locked_reason || 'claims_not_frozen');
        return;
      }
      if (Array.isArray(numeralsResponse)) {
        setNumerals(numeralsResponse);
      } else if (numeralsResponse?.locked) {
        setLocked(matterId, numeralsResponse.locked_reason || 'claims_not_frozen');
        return;
      }
      if (Array.isArray(termMappingsResponse)) {
        setTermMappings(termMappingsResponse);
      } else if (termMappingsResponse?.locked) {
        setLocked(matterId, termMappingsResponse.locked_reason || 'claims_not_frozen');
      }

      // Resume an in-flight or recently-completed AI figure-
      // generation job. Without this, refreshing the page mid-
      // generate lost the drafter's view: backend kept running but
      // the frontend had no job_id and no GenerationJobPill. Soft-
      // fails when no job is active so a clean matter reads
      // unaffected. The setGenerationJob action restarts polling
      // and renders the pill.
      //
      // When the resumed job is already ``completed`` (the worker
      // finished while the drafter was away), auto-open the
      // candidate-selection modal so the drafter sees the result
      // immediately instead of having to click the pill. For
      // queued / running jobs we leave the pill as the entry
      // point — popping a progress modal during quiet background
      // work would be obnoxious.
      try {
        const { job } = await getActiveGenerateAiJob(matterId);
        // Resume endpoint returns job_id (matches drawing_generation_jobs.id);
        // do NOT use job.id — that field doesn't exist in the response shape.
        if (job && job.job_id) {
          // Chunk-2 redesign: page reload no longer auto-opens the
          // modal even for completed jobs. The Recent Generations
          // panel is the durable surface; auto-open is now scoped
          // to the in-page running→completed transition only
          // (handled by the polling reducer in drawingStore).
          setGenerationJob(matterId, job.job_id, job);
        }
      } catch (e: unknown) {
        // Resume failure is non-fatal — the rest of the page works
        // and the drafter can re-trigger generation.
        // eslint-disable-next-line no-console
        console.warn('drawing job resume failed', e);
      }
    } catch (e: unknown) {
      const message = (e as { response?: { data?: { detail?: string } }; message?: string })
        ?.response?.data?.detail
        || (e as { message?: string })?.message
        || 'Failed to load drawing state.';
      setError(matterId, message);
    }
  }, [
    matterId,
    setHydrating, setLocked, setUnlocked, setError,
    setFigures, setSourceFigures, setNumerals, setTermMappings,
    setGenerationJob,
  ]);

  // SEC-F02 (Phase 28 3-I) — surface the raw errorMessage to the
  // developer console in dev-mode only so the leak-collapse at
  // the render site doesn't hide the failure cause during local
  // debugging. Production builds drop the effect body via the
  // NODE_ENV branch.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && errorMessage) {
      // eslint-disable-next-line no-console
      console.error('[DrawingPage] raw errorMessage:', errorMessage);
    }
  }, [errorMessage]);

  useEffect(() => {
    hydrate();
    return () => {
      // Reset on unmount so the next matter open starts clean.
      reset();
    };
  }, [hydrate, reset]);

  // ── Renders ──────────────────────────────────────────────────────

  // BUG-025 — derive frozen count from the live figures array
  // rather than from the rollup snapshot. ``refreshLists`` re-
  // fetches figures + numerals + term mappings after every
  // freeze / unfreeze, so this expression updates immediately;
  // ``rollup`` is only set on initial hydration and stays stale
  // until the next workbench reload, which made the
  // 'Open Specification →' header button never appear.
  const frozenCount = useMemo(
    () => figures.filter((f) => f.is_frozen).length,
    [figures],
  );
  // BUG-006 — same treatment for "N unanalyzed". The figure
  // summary's ``analyze_bundle_summary`` is null until analyze
  // has run; refreshLists() (fired by AnalyzeFigureModal on
  // success) re-pulls the figures array with the freshly
  // populated bundle, so this derived count drops by one the
  // instant analyze completes — no more "must navigate away
  // and back to refresh" symptom. The previous path read from
  // ``rollup.unanalyzed_count`` (a server snapshot taken on
  // hydrate) + an optimistic decrementUnanalyzedCount() call
  // in the modal that floored at 0 — fragile when multiple
  // analyzes ran in the same session or a refetch returned a
  // different rollup figure-count.
  const unanalyzedCount = useMemo(
    () => figures.filter((f) => !f.analyze_bundle_summary).length,
    [figures],
  );

  // Header action stack — Generate + Upload were previously
  // bottom-pinned inside FigureGallery, eating valuable left-rail
  // real estate. Hoisted into the top header alongside the Export
  // dropdown so the rail can grow into the freed space. The hidden
  // file input lives here too because the trigger button does.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const onPickUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);
  const onFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Reset so the same filename can be re-picked after a failed
      // upload — without this, ChangeEvent doesn't fire on the
      // second pick.
      e.target.value = '';
      if (file) void uploadFigure(matterId, file);
    },
    [matterId, uploadFigure],
  );

  // Phase 11 slice 11C — Export dropdown items. Memoised on the
  // matter id + frozen count so the menu re-renders only when the
  // PDF item's enabled state can actually change.
  const exportMenuItems = useMemo(
    () => buildDrawingExportMenuItems({
      matterId,
      frozenCount,
    }),
    [matterId, frozenCount],
  );

  const Header = (
    <div className="flex items-center justify-between border-b border-zinc-200 bg-white px-6 py-3">
      <div className="flex items-center gap-3">
        {onBack && (
          <button
            type="button"
            className={outlineBtn}
            onClick={guardedOnBack}
            data-testid="drawings-back"
            aria-label="Back to matter workflow"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        )}
        <h1 className="text-lg font-semibold text-zinc-900">Drawings</h1>
        {hydrationStatus === 'unlocked' && rollup && (
          <span className="ml-3 text-xs text-zinc-500">
            {figures.length} figure{figures.length === 1 ? '' : 's'}
            {frozenCount > 0 && ` · ${frozenCount} frozen`}
            {unanalyzedCount > 0 && ` · ${unanalyzedCount} unanalyzed`}
            {rollup.issues_count > 0 &&
              ` · ${rollup.issues_count} verify issue${rollup.issues_count === 1 ? '' : 's'}`}
          </span>
        )}
        {/* CHUNK 3.A — SaveStatusIndicator moved to a dedicated
            bottom strip (see WorkbenchStatusStrip below); the
            header stays clean for matter-level chrome. */}
      </div>
      <div className="flex items-center gap-2">
        {/* Compact auto-saved indicator. Replaces the bottom
            strip's left-half so the rail recovers that vertical
            line. The full bottom strip still renders when the
            workbench is offline (queue chip + discard escape
            hatch). */}
        {/* FT-006 (B2) — pass pendingMutationCount so the indicator
            can distinguish "nothing to save yet" (mount window;
            keep the chip quiet) from "queue has dirty entries
            that haven't reached the server" (real unsaved
            state). */}
        <CoursesLink onOpenCourses={onOpenCourses} testId="drawings-courses-button" />
        <HeaderSaveStatus
          status={saveStatus}
          lastSavedAt={lastSavedAt}
          pendingMutationCount={pendingMutationCount}
        />
        {/* Generate with AI + Upload from disk hoisted out of
            FigureGallery's bottom action stack so the rail has
            more space for IN DRAWING + AVAILABLE FROM IDF +
            RECENT GENERATIONS. */}
        <button
          type="button"
          onClick={openGenerationModal}
          className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-[#002FA7] hover:bg-zinc-50 transition-colors"
          data-testid="generate-ai"
          title="Generate a figure with AI"
        >
          <Sparkles className="h-3.5 w-3.5" />
          Generate with AI
        </button>
        <button
          type="button"
          onClick={onPickUpload}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
          data-testid="upload-figure"
          title="Upload a figure from disk"
        >
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadIcon className="h-3.5 w-3.5" />}
          {uploading ? 'Uploading…' : 'Upload from disk'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          className="sr-only"
          onChange={onFileChange}
          data-testid="upload-input"
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={outlineBtn}
              data-testid="drawings-export-trigger"
              aria-label="Export drawings"
              title="Export drawings"
            >
              <Download className="h-4 w-4" />
              Export
              <ChevronDown className="h-3 w-3" aria-hidden />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[16rem]">
            {exportMenuItems.map((item) => (
              <DropdownMenuItem
                key={item.key}
                disabled={item.disabled}
                asChild
                data-testid={`drawings-export-item-${item.key}`}
                title={item.tooltip}
              >
                <a
                  href={item.disabled ? undefined : item.href}
                  // HR-P2.3 — ``openMode`` from the helper
                  // controls anchor behaviour:
                  //  - ``download`` items keep the default same-
                  //    tab nav; the backend's
                  //    Content-Disposition: attachment intercepts
                  //    so the download dialog opens. ``download``
                  //    attribute primes the suggested filename.
                  //  - ``preview`` items target ``_blank`` so
                  //    the browser opens the inline-disposition
                  //    PDF in a new tab. ``rel="noopener
                  //    noreferrer"`` prevents the new tab from
                  //    accessing window.opener.
                  download={
                    item.openMode === 'download'
                      ? item.filename : undefined
                  }
                  target={
                    item.openMode === 'preview' ? '_blank' : undefined
                  }
                  rel={
                    item.openMode === 'preview'
                      ? 'noopener noreferrer' : undefined
                  }
                  // Disabled items shouldn't trigger navigation
                  // even if the keyboard reaches them.
                  onClick={(e) => {
                    if (item.disabled) e.preventDefault();
                  }}
                  aria-disabled={item.disabled || undefined}
                  className="flex w-full flex-col items-start gap-0.5"
                >
                  <span className="text-sm">{item.label}</span>
                  {item.tooltip && (
                    <span className="text-xs text-zinc-500">
                      {item.tooltip}
                    </span>
                  )}
                </a>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {/* Spec entry — only when at least one drawing is frozen.
            Spec module unlocks on any drawing_figures.is_frozen=TRUE
            (mirrors backend/spec.py:is_spec_unlocked). BUG-025 —
            gate on ``frozenCount`` (live from the figures array)
            instead of the stale rollup snapshot so the button
            appears immediately after the freeze response lands. */}
        {onOpenSpec && hydrationStatus === 'unlocked' &&
          frozenCount > 0 && (
            <button
              type="button"
              className="inline-flex h-9 items-center justify-center gap-1 rounded-sm bg-zinc-900 px-3 text-sm font-medium text-white hover:bg-zinc-800 transition-colors"
              onClick={onOpenSpec}
              data-testid="drawings-open-spec"
              aria-label="Open Specification workbench"
              title="Open Specification workbench"
            >
              Open Specification →
            </button>
          )}
        {onOpenSettings && (
          <button
            type="button"
            className={outlineBtn}
            onClick={onOpenSettings}
            data-testid="drawings-open-settings"
            aria-label="Drawing module settings"
            title="Drawing module settings"
          >
            <SettingsIcon className="h-4 w-4" />
            Settings
          </button>
        )}
      </div>
    </div>
  );

  if (viewportTooNarrow) {
    return (
      <ViewportTooNarrow
        moduleName="Drawing workbench"
        detail={
          'The figure canvas, sidebar gallery, and reference panel need at least ' +
          `${VIEWPORT_MIN_WIDTH_PX}px of horizontal space. Open this matter on a ` +
          'desktop to start drafting drawings.'
        }
        header={Header}
        testId="drawings-viewport-stub"
      />
    );
  }

  if (hydrationStatus === 'idle' || hydrationStatus === 'loading') {
    return (
      <div className="min-h-screen bg-zinc-50">
        {Header}
        <div
          className="flex min-h-[calc(100vh-3.5rem)] items-center justify-center text-sm text-zinc-500"
          data-testid="drawings-loading"
        >
          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
          Loading drawing workbench…
        </div>
      </div>
    );
  }

  if (hydrationStatus === 'error') {
    return (
      <div className="min-h-screen bg-zinc-50">
        {Header}
        <div
          className="mx-auto max-w-xl px-6 py-16 text-center"
          data-testid="drawings-error"
        >
          <AlertTriangle className="mx-auto mb-4 h-10 w-10 text-amber-500" aria-hidden />
          <h2 className="mb-2 text-base font-semibold text-zinc-900">
            Could not load drawing workbench
          </h2>
          {/* SEC-F02 (Phase 28 3-I) — sanitise. Long / path-bearing
              backend detail strings collapse to a generic message;
              the dev-only console.error effect below keeps the
              raw value available for developers. */}
          <p className="mb-4 text-sm text-zinc-600">
            {sanitizeApiError(errorMessage ?? undefined)}
          </p>
          <button type="button" className={outlineBtn} onClick={hydrate}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (hydrationStatus === 'locked') {
    return (
      <div className="min-h-screen bg-zinc-50">
        {Header}
        <div
          className="mx-auto max-w-xl px-6 py-16 text-center"
          data-testid="drawings-locked"
        >
          <Lock className="mx-auto mb-4 h-10 w-10 text-zinc-400" aria-hidden />
          <h2 className="mb-2 text-base font-semibold text-zinc-900">
            Drawing unlocks when claims freeze
          </h2>
          <p className="text-sm text-zinc-600">
            {lockedReason === 'claims_not_frozen'
              ? 'Finish Stage 3 and freeze the claim set to start working on drawings.'
              : `Drawing is currently locked: ${lockedReason}.`}
          </p>
          {onBack && (
            <button type="button" className={`${outlineBtn} mt-6`} onClick={onBack}>
              <ArrowLeft className="h-4 w-4" />
              Return to matter
            </button>
          )}
        </div>
      </div>
    );
  }

  // hydrationStatus === 'unlocked'
  // BUG-031 — workbench is locked to the viewport: outer column-flex
  // with overflow-hidden so a tall canvas / numerals list never
  // scrolls the page. Each column carries its own scroll inside
  // (the asides via Section's internal overflow-y-auto, the canvas
  // via its viewport's transform-based pan). The status strip at
  // the bottom and the header / SpecRevisionBanner at the top are
  // flex-shrink-0 so they stay pinned regardless of content height.
  return (
    <div
      className="flex h-screen flex-col overflow-hidden bg-zinc-50"
      data-testid="drawings-page"
    >
      <div className="flex-shrink-0">{Header}</div>
      {matterId && (
        <div className="flex-shrink-0">
          <SpecRevisionBanner
            matterId={matterId}
            stage="stage_5"
            onEditTarget={(p) => {
              // Activate the figure card the proposal targets so the
              // existing canvas + reference-panel edit flow is one
              // click away.
              if (p.target_drawing_figure_id) {
                setActiveFigureId(p.target_drawing_figure_id);
              }
            }}
          />
        </div>
      )}
      <div className="grid min-h-0 flex-1 grid-cols-[280px_1fr_320px] overflow-hidden">
        <aside
          className="flex h-full min-h-0 flex-col overflow-hidden border-r border-zinc-200 bg-white"
          data-testid="drawings-sidebar"
          aria-label="Figure gallery"
        >
          <FigureGallery
            matterId={matterId}
            figures={figures}
            sourceFigures={sourceFigures}
            activeFigureId={activeFigureId}
            onSelect={setActiveFigureId}
            onImport={(id) => { void importFromIdf(matterId, id); }}
            onSync={(id) => { void syncSource(matterId, id); }}
            onDiscard={(id) => discardFigure(matterId, id)}
            onAcknowledgeSourceRemoved={(id) => { void acknowledgeSourceRemoved(matterId, id); }}
            pendingImportSourceIds={pendingImportSourceIds}
            pendingSyncIds={pendingSyncIds}
          />
        </aside>
        <main
          className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-zinc-100"
          data-testid="drawings-canvas-area"
          aria-label="Figure canvas"
        >
          {/* Konva-migration soak — default-on as of
              docs/WORKBENCH_SIMPLIFICATION_NOTES.md "Canvas
              migration — default flipped to Konva". ?canvas=svg
              opts back into the legacy Canvas.tsx for emergency
              rollback during the soak window; ?canvas=konva is
              still recognised as a no-op for backwards-compat
              with share links from the opt-in era. */}
          {FEATURES.USE_KONVA_CANVAS ? (
            <KonvaCanvas matterId={matterId} figure={activeFigure} />
          ) : (
            <Canvas matterId={matterId} figure={activeFigure} />
          )}
        </main>
        <aside
          className="flex h-full min-h-0 flex-col overflow-hidden border-l border-zinc-200 bg-white"
          data-testid="drawings-reference-panel"
          aria-label="Reference panel"
        >
          <ReferencePanel
            matterId={matterId}
            numerals={numerals}
            termMappings={termMappings}
            activeTab={referencePanelTab}
            onTabChange={setReferencePanelTab}
            activeFigure={activeFigure}
            onRetryPriming={(id) => { void reprimeFigure(matterId, id); }}
            reprimePending={!!activeFigureId && pendingSyncIds.has(activeFigureId)}
            figures={figures}
            canvasPrimitives={canvasPrimitives}
          />
        </aside>
      </div>
      {/* CHUNK 3.A — workbench-level status strip. Now conditional
          — only renders when something needs surfacing (offline OR
          pending mutations). The Auto-saved time hoisted into the
          top header (HeaderSaveStatus). The bottom strip stays as
          the place for the offline-queue chip + discard escape
          hatch when those apply. */}
      {(saveStatus === 'offline' || pendingMutationCount > 0) && (
        <div className="flex-shrink-0">
          <WorkbenchStatusStrip
            status={saveStatus}
            pending={pendingMutationCount}
            lastSavedAt={lastSavedAt}
            matterId={matterId}
          />
        </div>
      )}
      {mutationError && (
        <MutationErrorBanner message={mutationError} onDismiss={() => setMutationError(null)} />
      )}
      <ToastSnackbar />
      <GenerationJobPill />
      {generationModalOpen && <GenerateAiModal matterId={matterId} />}
      {analyzeModalFigureId !== null && <AnalyzeFigureModal matterId={matterId} />}
      {claimEditModalClaimId !== null && <ClaimEditModal matterId={matterId} />}
    </div>
  );
}

/** Compact auto-save chip rendered inline in the top header.
 * Replaces the bottom strip's left-half so the figure-gallery
 * rail recovers a line of vertical space. The full bottom strip
 * still surfaces when the workbench is offline OR has pending
 * mutations (queue chip + discard escape hatch). */
function HeaderSaveStatus({
  status, lastSavedAt, pendingMutationCount = 0,
}: {
  status: 'idle' | 'saving' | 'saved' | 'offline';
  lastSavedAt: number | null;
  pendingMutationCount?: number;
}) {
  const fmtTime = (ms: number | null): string => {
    if (!ms) return '';
    try {
      return new Date(ms).toLocaleTimeString([], {
        hour: 'numeric', minute: '2-digit',
      });
    } catch {
      return '';
    }
  };
  // ``offline`` and ``pending`` (the latter via WorkbenchStatusStrip
  // when status===saved && pendingMutationCount > 0) get a louder
  // surface in the bottom strip; the header chip in those cases
  // would duplicate noise. Hide here when either applies — the
  // parent already gates ``offline`` separately.
  if (status === 'offline') return null;
  // FT-006 (B2) — the chip used to render "Not yet saved" for the
  // first 1-2s after mount because the store boots with
  // status='idle' + lastSavedAt=null, and the user re-entering the
  // module saw a false stale-state flash before the queue confirmed
  // (often: with nothing to flush). Now we only surface "Not yet
  // saved" when there are actual dirty entries waiting on the
  // queue; the empty-queue idle state hides the chip entirely so
  // the workbench loads quiet.
  if (status === 'idle' && pendingMutationCount === 0) return null;
  const text =
    status === 'saving' ? 'Saving…'
    : status === 'saved' && lastSavedAt ? `Auto-saved ${fmtTime(lastSavedAt)}`
    : 'Not yet saved';
  return (
    <span
      className="hidden md:inline-flex items-center gap-1 px-2 text-[11px] text-zinc-500"
      data-testid="header-save-status"
    >
      <span aria-hidden>○</span>
      {text}
    </span>
  );
}


/** CHUNK 3.A — calmer workbench status bar at the bottom edge.
 * Auto-saved timestamp on the left, offline-queue chip on the
 * right. Visually quieter than the previous header pill so the
 * top toolbar stays focused on matter-level chrome. */
function WorkbenchStatusStrip({
  status, pending, lastSavedAt, matterId,
}: {
  status: 'idle' | 'saving' | 'saved' | 'offline';
  pending: number;
  lastSavedAt: number | null;
  matterId: string;
}) {
  const fmtTime = (ms: number | null): string => {
    if (!ms) return '';
    try {
      return new Date(ms).toLocaleTimeString([], {
        hour: 'numeric', minute: '2-digit',
      });
    } catch {
      return '';
    }
  };
  const left =
    status === 'saving'
      ? <span className="text-zinc-500">○ Saving…</span>
      : status === 'saved' && lastSavedAt
        ? <span className="text-zinc-500">○ Auto-saved {fmtTime(lastSavedAt)}</span>
        : status === 'offline'
          ? <span className="text-zinc-500">○ Offline</span>
          : <span className="text-zinc-400">○ Not yet saved</span>;
  return (
    <div
      className="flex items-center justify-between border-t border-zinc-200 bg-white px-3 py-1.5 text-[11px]"
      role="status"
      data-testid="workbench-status-strip"
    >
      {left}
      {status === 'offline' && (
        <span
          className="inline-flex items-center gap-2 rounded-sm bg-amber-50 px-2 py-0.5 font-medium text-amber-800 ring-1 ring-amber-200"
          data-testid="workbench-offline-chip"
        >
          <span>
            working offline · {pending} change{pending === 1 ? '' : 's'} queued
          </span>
          {/* BUG-033 — escape hatch for queues stuck behind a
              permanently-rejected batch. The auto-drain on 4xx
              already handles new failures, but a queue from a
              prior session still needs explicit user consent
              before discarding. */}
          {pending > 0 && (
            <button
              type="button"
              className="ml-1 rounded-sm border border-amber-300 bg-white/70 px-1.5 text-[10px] font-medium text-amber-800 hover:bg-white"
              onClick={() => {
                if (typeof window === 'undefined') return;
                const ok = window.confirm(
                  `Discard ${pending} pending canvas change${pending === 1 ? '' : 's'}? `
                  + 'They have not been saved to the server. This cannot be undone.',
                );
                if (!ok) return;
                const dropped = discardPendingMutations(matterId);
                useDrawingStore.setState({
                  mutationError: dropped > 0
                    ? `Discarded ${dropped} pending change${dropped === 1 ? '' : 's'} from the offline queue.`
                    : null,
                });
              }}
              data-testid="workbench-discard-queue"
              title="Discard the queued canvas changes (irreversible)."
            >
              Discard
            </button>
          )}
        </span>
      )}
    </div>
  );
}

function MutationErrorBanner({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex max-w-md items-start gap-2 rounded-md border border-amber-300 bg-white px-3 py-2 shadow-md"
      role="alert"
      data-testid="drawings-mutation-error"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" aria-hidden />
      <p className="flex-1 text-xs text-zinc-700">{message}</p>
      <button
        type="button"
        className="text-[11px] font-medium text-zinc-500 hover:text-zinc-900"
        onClick={onDismiss}
      >
        Dismiss
      </button>
    </div>
  );
}

/** Slice 8E.2.a — ephemeral success-toast snackbar. Stacks ABOVE
 * the mutation-error banner (different bottom offset) so a
 * back-to-back error + success doesn't overlap. Auto-clears via
 * the store's showToast timer. */
function ToastSnackbar() {
  const message = useDrawingStore((s) => s.toastMessage);
  const clearToast = useDrawingStore((s) => s.clearToast);
  if (!message) return null;
  return (
    <div
      className="fixed bottom-16 right-4 z-50 flex max-w-md items-start gap-2 rounded-md border border-emerald-300 bg-white px-3 py-2 shadow-md"
      role="status"
      aria-live="polite"
      data-testid="drawings-toast"
    >
      <p className="flex-1 text-xs text-zinc-700">{message}</p>
      <button
        type="button"
        className="text-[11px] font-medium text-zinc-500 hover:text-zinc-900"
        onClick={clearToast}
      >
        Dismiss
      </button>
    </div>
  );
}
